import React from 'react';

// Import the SVG icons as requested
import HappyIcon from '../../../../Assets/Foundational/picsvg_download(1).svg';
import NeutralIcon from '../../../../Assets/Foundational/picsvg_download(2).svg';
import QuestionIcon from '../../../../Assets/Foundational/picsvg_download(3).svg';

// Fixed pixel sizes for PDF compatibility
const ICON_SIZE = '56px';

/**
 * SelfAssessmentPage component for the HPC report
 * Displays student's self-assessment with SVG icon-based responses
 *
 * @param {Object} assignment - The assignment data containing self-assessment
 * @param {Number} pageNumber - Page number for the report
 */
const SelfAssessmentPage = ({ assignment }) => {
    // Ensure we have data to display
    if (!assignment) {
        return <div className="report-page">Loading Self-Assessment...</div>;
    }

    // Extract self-assessment data from assignment
    const selfAssessment = assignment.self_assessment || {};

    // Debug: Log the self assessment data
    console.log("SelfAssessmentPage - assignment:", assignment);
    console.log("SelfAssessmentPage - selfAssessment:", selfAssessment);

    // Helper function to check if a value is selected and return inline styles for PDF compatibility
    const getSelectedStyle = (field, value) => {
        // Handle different data formats
        let actualValue = selfAssessment[field];

        // Map old field names to new ones based on actual data structure
        const fieldMapping = {
            'followed_instructions': 'followed_instructions',
            'liked_work': 'liked',
            'asked_for_help': 'easy',
            'tried_my_best': 'tried_my_best',
            'proud_of_work': 'proud_of_work',
            'want_to_repeat': 'want_to_repeat',
            'liked_groupwork': 'liked_groupwork',
            'asked_classmate_help': 'asked_classmate_help'
        };

        // Try mapped field name if original doesn't exist
        if (actualValue === undefined && fieldMapping[field]) {
            actualValue = selfAssessment[fieldMapping[field]];
        }

        let isSelected = false;

        // Handle boolean to string conversion
        if (typeof actualValue === 'boolean') {
            if (value === 'Yes' || value === true) {
                isSelected = actualValue === true;
            } else if (value === 'No' || value === false) {
                isSelected = actualValue === false;
            }
        } else {
            isSelected = actualValue === value;
        }

        // Return inline styles - use border instead of ring for PDF compatibility
        if (isSelected) {
            return {
                border: '3px solid #F97316',
                backgroundColor: '#FED7AA',
                borderRadius: '50%',
                boxSizing: 'border-box'
            };
        }

        // Dashed border for unselected items as per reference image
        return {
            border: '2px dashed #D1D5DB',
            borderRadius: '50%',
            boxSizing: 'border-box'
        };
    };

    // Helper function to check if a value is selected (for text styling)
    const isSelected = (field, value) => {
        let actualValue = selfAssessment[field];

        const fieldMapping = {
            'followed_instructions': 'followed_instructions',
            'liked_work': 'liked',
            'asked_for_help': 'easy',
            'tried_my_best': 'tried_my_best',
            'proud_of_work': 'proud_of_work',
            'want_to_repeat': 'want_to_repeat',
            'liked_groupwork': 'liked_groupwork',
            'asked_classmate_help': 'asked_classmate_help'
        };

        if (actualValue === undefined && fieldMapping[field]) {
            actualValue = selfAssessment[fieldMapping[field]];
        }

        if (typeof actualValue === 'boolean') {
            if (value === 'Yes' || value === true) {
                return actualValue === true;
            } else if (value === 'No' || value === false) {
                return actualValue === false;
            }
        }

        return actualValue === value;
    };

    // Reusable row component for assessment questions
    const AssessmentRow = ({ label, field, isLast = false }) => (
        <tr className={isLast ? '' : 'border-b border-orange-200'}>
            <td className="p-2 w-1/2 align-middle border-r border-orange-200">
                {label}
            </td>
            <td className="p-2 align-middle">
                <div className="flex justify-around items-center">
                    <div className="flex flex-col items-center">
                        <div
                            style={{
                                width: ICON_SIZE,
                                height: ICON_SIZE,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ...getSelectedStyle(field, 'Yes')
                            }}
                        >
                            <img src={HappyIcon} alt="yes" style={{ width: ICON_SIZE, height: ICON_SIZE }} />
                        </div>
                        <span
                            className="text-xs mt-1"
                            style={{
                                fontWeight: isSelected(field, 'Yes') ? 700 : 500,
                                color: isSelected(field, 'Yes') ? '#9A3412' : '#6B7280'
                            }}
                        >yes</span>
                    </div>
                    <div style={{ height: '48px', borderRight: '1px dotted #9CA3AF' }}></div>
                    <div className="flex flex-col items-center">
                        <div
                            style={{
                                width: ICON_SIZE,
                                height: ICON_SIZE,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ...getSelectedStyle(field, 'No')
                            }}
                        >
                            <img src={NeutralIcon} alt="no" style={{ width: ICON_SIZE, height: ICON_SIZE }} />
                        </div>
                        <span
                            className="text-xs mt-1"
                            style={{
                                fontWeight: isSelected(field, 'No') ? 700 : 500,
                                color: isSelected(field, 'No') ? '#9A3412' : '#6B7280'
                            }}
                        >no</span>
                    </div>
                    <div style={{ height: '48px', borderRight: '1px dotted #9CA3AF' }}></div>
                    <div className="flex flex-col items-center">
                        <div
                            style={{
                                width: ICON_SIZE,
                                height: ICON_SIZE,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                ...getSelectedStyle(field, 'Not sure')
                            }}
                        >
                            <img src={QuestionIcon} alt="not sure" style={{ width: ICON_SIZE, height: ICON_SIZE }} />
                        </div>
                        <span
                            className="text-xs mt-1"
                            style={{
                                fontWeight: isSelected(field, 'Not sure') ? 700 : 500,
                                color: isSelected(field, 'Not sure') ? '#9A3412' : '#6B7280'
                            }}
                        >not sure</span>
                    </div>
                </div>
            </td>
        </tr>
    );

    return (
        <div className="report-page relative font-[poppins] bg-white" style={{
            width: '210mm',
            height: '297mm',
            minHeight: '297mm',
            padding: '15mm 20mm',
            margin: '0 auto',
            fontSize: '10pt',
            lineHeight: '1.4',
            color: '#333333',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {/* Self-Assessment Header */}
            <div className="bg-orange-500 text-white py-3 px-4 text-center font-bold text-lg">
                Self-Assessment
            </div>

            {/* Main container with borders */}
            <div className="border border-t-0 border-orange-300 mb-6">
                <div className="border-b border-orange-200 p-3 bg-orange-50">
                    <p>Please answer the following regarding the activity you just completed.</p>
                    <p className="font-medium pt-3">Colour the emoji for each statement.</p>
                </div>

                {/* Assessment Table */}
                <table className="w-full border-collapse">
                    <tbody>
                        <AssessmentRow label="I followed my teacher's instructions." field="followed_instructions" />
                        <AssessmentRow label="I liked doing this work." field="liked_work" />
                        <AssessmentRow label="I asked for help if I didn't understand." field="asked_for_help" />
                        <AssessmentRow label="I tried my best in this task." field="tried_my_best" />
                        <AssessmentRow label="I am proud of my work." field="proud_of_work" />
                        <AssessmentRow label="I want to do this task again." field="want_to_repeat" />
                        <AssessmentRow label="I liked working with my classmate/s." field="liked_groupwork" />
                        <AssessmentRow label="I could ask my classmates for help, and they helped me." field="asked_classmate_help" isLast={true} />
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SelfAssessmentPage;
